import type { ShapePoint, ShapeVertex } from '@repo/api-contracts';
import { segmentOf, segmentPointAt, splitCubicAt } from './cubic-segment';

const plain = (point: ShapePoint): ShapePoint => ({ xMm: point.xMm, yMm: point.yMm });

/**
 * 구간 segmentIndex(그 점 → 다음 점)의 t 에 점을 끼운다. 새 점은 segmentIndex + 1 자리에 들어간다.
 * 곡선 구간은 de Casteljau 로 나눠 모양이 그대로이고(양 끝 점의 손잡이도 줄어든다), 직선 구간은 그 자리의 점을 넣는다.
 * 원래 없던 끝 점 손잡이는 새로 만들지 않는다 — 없는 제어점은 끝점과 같아 나눈 값도 끝점 그대로다.
 * 구간이 없거나 t 가 0..1 밖이면 null.
 */
export const insertVertexAt = (
  points: readonly ShapeVertex[],
  segmentIndex: number,
  t: number,
): ShapeVertex[] | null => {
  const segment = segmentOf(points, segmentIndex);
  const start = points[segmentIndex];
  const endIndex = (segmentIndex + 1) % points.length;
  const end = points[endIndex];
  if (segment === null || start === undefined || end === undefined || t <= 0 || t >= 1) return null;
  if (!segment.curved) {
    const next = [...points];
    next.splice(segmentIndex + 1, 0, plain(segmentPointAt(segment, t)));
    return next;
  }
  const { q0, r0, s, r1, q2 } = splitCubicAt(segment, t);
  const updated = points.map((point, index): ShapeVertex => {
    if (index === segmentIndex)
      return start.handleOut === undefined ? point : { ...point, handleOut: q0 };
    if (index === endIndex) return end.handleIn === undefined ? point : { ...point, handleIn: q2 };
    return point;
  });
  updated.splice(segmentIndex + 1, 0, { ...plain(s), handleIn: r0, handleOut: r1 });
  return updated;
};
